import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { InstantSearchApi } from 'react-instantsearch'
import type { IIap } from '~/types/iap'

import columns from './columns'

import PanelContent from '../PanelContent'
import SynchronizeButton from '../SynchronizeButton'

import { updateSubType } from '~/api/iap'
import AlgoliaTable from '~/components/Algolia/Table'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Panel from '~/components/Panel'
import ViewTitle from '~/components/ViewTitle'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { IAP_SUB_TYPE } from '~/types/iap'
import { getIapFilters } from '~/utils/algolia'
import { transformByDeviceType } from '~/utils/application'
import { isFocusedCellActions } from '~/utils/table'
import { displayToast } from '~/utils/toast'

import './styles.scss'

export interface IUpdateIapSubTypeArgs {
  iapId: string
  isChecked: boolean
}

const refinements = [
  { attribute: 'sub_type', label: 'Sub type' },
  { attribute: 'status', label: 'Status' },
  { attribute: 'price', label: 'Price' },
]

const IapProducts = (): ReactElement => {
  /** Local state */

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  const [instantSearchApi, setInstantSearchApi] = useState<InstantSearchApi>()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const [selectedIap, setSelectedIap] = useState<IIap>()

  /** Queries & Mutations */

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { mutateAsync: updateIapSubTypeMutation } = useMutation({ mutationFn: updateSubType })

  /** Effects */

  useDocumentTitle('IAP Products')

  const handleClosePanel = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll()
    }
    setSelectedIap(undefined)
  }, [])

  const handleClickOutside = useCallback(
    (event: any) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current?.contains(event.target) || panelRef.contains(event.target))) ||
        !document.getElementById('root')?.contains(event.target)
      ) {
        return
      }
      handleClosePanel()
    },
    [panelRef, handleClosePanel]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [panelRef, handleClickOutside])

  /** Handlers */

  const configurationFilters = getIapFilters({ appKey, deviceType })

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (
      !isFocusedCellActions(evt) &&
      gridRef.current?.api &&
      gridRef.current.api.getFocusedCell()?.column.getColId() !== 'sub_type'
    ) {
      setSelectedIap(evt.data)
    }
  }

  const handleSynchronizeSuccess = () => {
    instantSearchApi?.refresh()
    gridRef.current?.api?.refreshCells({ force: true })
  }

  const handleTableInit = (instantSearchApi: InstantSearchApi) => setInstantSearchApi(instantSearchApi)

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  const handleConsumableCheck = async ({ iapId, isChecked }: IUpdateIapSubTypeArgs) => {
    if (appSummary?.app_id) {
      try {
        await updateIapSubTypeMutation({
          appId: appSummary.app_id,
          iapId,
          subType: isChecked ? IAP_SUB_TYPE.CONSUMABLE : IAP_SUB_TYPE.NON_CONSUMABLE,
        })
        displayToast('In-app sub type successfully updated', 'success')
      } catch (e) {
        displayToast('In-app sub type could not be updated', 'error')
        captureException(e)
      }
    }
  }

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="iap-products-view" withPadding>
        <ViewTitle className="layout-view-title">
          <span>In-App purchases / Products</span>
          <SynchronizeButton
            appId={appSummary?.app_id}
            deviceType={deviceType}
            onSynchronizeSuccess={handleSynchronizeSuccess}
          />
        </ViewTitle>
        <ContentLayout className="iap-products-view__content" loading={isAppSummaryLoading}>
          <AlgoliaTable
            className="with-min-height"
            columnDefs={columns}
            configurationFilters={configurationFilters}
            context={{ onConsumableCheck: handleConsumableCheck }}
            entriesName="iap-products"
            hitsPerPage={10}
            indexName={ALGOLIA_INDEX_NAMES.IAP_PRODUCTS}
            onInit={handleTableInit}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            refinements={refinements}
            tableRef={tableRef}
            withSearchBarAutoFocus
          />
        </ContentLayout>
        <Panel
          className="iap-panel"
          isOpen={!!selectedIap}
          onClosePanel={handleClosePanel}
          panelRef={contentRefCallback}
        >
          <PanelContent iap={selectedIap} />
        </Panel>
      </Layout>
    </>
  )
}

export default IapProducts
